import { platform } from '@amp/web-apps-utils';
import type { Logger } from '@amp/web-apps-logger/src';
import type { NativeApps } from '~/jet/models';

export const DEFAULT_ORIGIN = 'https://classical.music.apple.com';
const NATIVE_APPS_ORIGINS = {
    music: 'https://music.apple.com',
    tv: 'https://tv.apple.com',
    podcasts: 'https://podcasts.apple.com',
    classical: 'https://classical.music.apple.com',
};

/**
 * Consistently set the origin URL to DEFAULT_ORIGIN.
 *
 * @param url url from app
 * @returns url string with DEFAULT_ORIGIN
 */
const normalizeUrl = (url: string, appType: NativeApps): string => {
    const incomingUrl = new URL(url);
    let pathname = incomingUrl.pathname;

    const isHomeTab =
        pathname.match(/(\/listen\-now)/) || pathname.match(/(\/home)/);

    // For the home tab we need to manually set the url to `listen-now`
    // to deep link into native app + support older clients
    if (isHomeTab) {
        pathname = 'listen-now';
    }

    const searchParams = new URLSearchParams(incomingUrl.search);

    const searchParmsVal = searchParams.toString();

    const urlString = searchParmsVal
        ? `${pathname}?${searchParmsVal}`
        : pathname;

    return new URL(urlString, NATIVE_APPS_ORIGINS[appType]).toString();
};

/**
 * Opens launches the native app with the correct deeplink if page exists.
 * @param jet - jet instance
 * @param url - url to page that needs to be opened in native
 * @param log - logger instance
 */
export const launchApp = async (
    url: string,
    appType?: NativeApps,
    log?: Logger,
): Promise<void> => {
    const appUrl = appType ? normalizeUrl(url, appType) : url;

    if (appUrl) {
        try {
            platform.launchClient(appUrl);
        } catch (error) {
            log.info(`Error launching ${appUrl} in native app.`);
        }
    } else {
        if (log) {
            log.info(`${appUrl} not found in native app.`);
        }
    }
};
